/**
 * Account-management list — Publishers / Admins / Super Creators.
 *
 * One component, three routes (parameterized by `role`), mirroring the
 * publisher dashboard's single views/user/list serving /publisher/list,
 * /admin/list, /super-creator/list. Backed by GET /users?role=… Ported
 * into admin.nurev.io 2026-06-09 (replaces the MigrationStub).
 */
import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon, AlertCircle, ChevronRight } from 'lucide-react';
import { useUsers } from '@/hooks/useAdminUsers';
import { AccountStatus, AdminUser, UserRole } from '@/api/adminUsers';
import {
  Badge,
  Card,
  Pagination,
  SectionLabel,
  Select,
  Skeleton
} from '@/components/ui/atoms';
import { fmtDuration, fmtNum } from '@/lib/format';

const STATUS_OPTIONS: Array<{ value: AccountStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'rejected', label: 'Rejected' }
];

export function UsersScreen({
  role,
  title,
  description,
  detailBase
}: {
  role: UserRole;
  title: string;
  description: string;
  detailBase: string;
}) {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<AccountStatus | ''>('');
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, error } = useUsers({
    role,
    search,
    status,
    page,
    limit: 20
  });

  const rows = data?.docs ?? [];
  const totalPages = data?.totalPages ?? 1;

  function onSearch(e: FormEvent) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-nurev-text">{title}</h1>
        <p className="mt-1 text-sm text-nurev-muted">{description}</p>
      </div>

      <Card>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <form onSubmit={onSearch} className="relative flex-1 sm:max-w-sm">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-nurev-muted" />
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search name, email, company…"
              className="w-full rounded-lg border border-nurev-border bg-white/[0.04] py-1.5 pl-9 pr-3 text-sm text-nurev-text placeholder:text-nurev-muted focus:border-amber-500/50 focus:outline-none"
            />
          </form>
          <Select
            value={status}
            onChange={v => {
              setPage(1);
              setStatus(v);
            }}
            options={STATUS_OPTIONS}
          />
        </div>

        {error ? (
          <div className="py-10 text-center">
            <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-400" />
            <p className="text-sm text-nurev-muted">
              Couldn't load accounts. Re-login if this persists.
            </p>
          </div>
        ) : (
          <>
            <SectionLabel>
              {fmtNum(data?.totalDocs)} {title.toLowerCase()}
              {isFetching && !isLoading ? ' · updating…' : ''}
            </SectionLabel>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                    <th className="py-2 pr-3 font-medium">Name</th>
                    <th className="py-2 pr-3 font-medium">Company</th>
                    <th className="py-2 pr-3 font-medium">Email</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 pr-3 text-right font-medium">Installs</th>
                    <th className="py-2 pr-3 text-right font-medium">Live</th>
                    <th className="py-2 pr-3 text-right font-medium">Live Time</th>
                    <th className="py-2 pr-3 text-right font-medium">Earnings</th>
                    <th className="py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i} className="border-b border-nurev-border/50">
                        <td className="py-2.5 pr-3" colSpan={9}>
                          <Skeleton className="h-4 w-full" />
                        </td>
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="py-10 text-center text-sm text-nurev-muted"
                      >
                        No accounts match.
                      </td>
                    </tr>
                  ) : (
                    rows.map((u: AdminUser) => (
                      <tr
                        key={u._id}
                        onClick={() => navigate(`${detailBase}/${u._id}`)}
                        className="cursor-pointer border-b border-nurev-border/50 transition hover:bg-white/[0.02]"
                      >
                        <td className="py-2.5 pr-3 text-nurev-text">
                          {u.name || '—'}
                          {u.isPrimary && (
                            <span className="ml-1.5 rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-medium text-amber-300">
                              primary
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 text-nurev-muted">
                          {u.companyName || '—'}
                        </td>
                        <td className="py-2.5 pr-3 text-nurev-muted">{u.email}</td>
                        <td className="py-2.5 pr-3">
                          <Badge value={u.status} />
                        </td>
                        <td className="py-2.5 pr-3 text-right text-nurev-text">
                          {fmtNum(u.installs)}
                        </td>
                        <td className="py-2.5 pr-3 text-right text-emerald-300">
                          {fmtNum(u.live)}
                        </td>
                        <td className="py-2.5 pr-3 text-right text-nurev-muted">
                          {fmtDuration(u.liveTime)}
                        </td>
                        <td className="py-2.5 pr-3 text-right text-nurev-text">
                          {fmtNum(u.earnings)}
                        </td>
                        <td className="py-2.5 text-right">
                          <ChevronRight className="ml-auto h-4 w-4 text-nurev-muted" />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            )}
          </>
        )}
      </Card>
    </div>
  );
}
